import React, { FC, Fragment, useState } from 'react';
import { Grid, IconButton, Tooltip } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import CheckCircleRoundedIcon from '@material-ui/icons/CheckCircleRounded';
import CreateRoundedIcon from '@material-ui/icons/CreateRounded';
import RemoveCircleRoundedIcon from '@material-ui/icons/RemoveCircleRounded';

import { Alert } from 'components';
import { CheckedMode, UseCheckedState } from 'types/app';
import {
  BulkUpdateContentMutation,
  BulkUpdateContentMutationVariables,
  ContentApprovalTypes,
  ContentFragment,
  useBulkUpdateContentMutation,
} from 'api';
import { useNotify } from 'hooks';
import { generateLink } from 'Links';
import { UseMutationOptions } from 'react-query';

enum Action {
  Approve,
  Remove,
  None,
}

export type UpdateContentMutationOptions = UseMutationOptions<
  BulkUpdateContentMutation,
  unknown,
  BulkUpdateContentMutationVariables
>;

type Variables = BulkUpdateContentMutationVariables;

interface Props extends Partial<UseCheckedState> {
  bulk?: boolean;
  count: number;
  variables?: Variables;
  contents: ContentFragment[];
  updateContentMutationOptions: UpdateContentMutationOptions;
}

const Actions: FC<Props> = ({
  bulk,
  count,
  checkedIds,
  checkedMode,
  resetCheckedState,
  variables,
  contents,
  updateContentMutationOptions,
}) => {
  const { t } = useTranslation();
  const notify = useNotify();
  const [action, setAction] = useState<Action>(Action.None);

  const approval = variables?.bulk_update.approval;
  const isSubmitted = approval === ContentApprovalTypes.Submitted;
  const actionCount = checkedMode === CheckedMode.OFF ? checkedIds.length : count;

  function getMessage(count: number) {
    return action === Action.Approve
      ? t('admin:contentsApproved', { count })
      : t('admin:contentRejected', { count });
  }

  const { mutate: updateContentMutation } = useBulkUpdateContentMutation({
    ...updateContentMutationOptions,
    onSuccess: data => {
      notify.info({ message: getMessage(data.bulkUpdateContent.success) });
    },
  });

  function getGroupTitle() {
    const groupId = variables?.bulk_update.content_group_list[0].group_id;
    let title = '';
    contents.forEach(content => {
      content.groups.forEach(group => {
        if (group.id === groupId) {
          title = group.title;
        }
      });
    });
    return title;
  }

  function handleCancel() {
    setAction(Action.None);
  }

  function handleContentRemoval() {
    variables.bulk_update.approval = ContentApprovalTypes.Removed;
    updateContentMutation(variables);
    setAction(Action.None);
    resetCheckedState && resetCheckedState();
  }

  function handleContentApproval() {
    variables.bulk_update.approval = ContentApprovalTypes.Approved;
    updateContentMutation(variables);
    setAction(Action.None);
    resetCheckedState && resetCheckedState();
  }

  if (approval === ContentApprovalTypes.Removed) {
    return null;
  }

  function renderAlerts() {
    if (bulk) {
      return (
        <Fragment>
          <Alert
            open={action === Action.Remove}
            onConfirm={handleContentRemoval}
            onCancel={handleCancel}
            message={t('admin:bulkContentRemoval', { count: actionCount })}
            confirm={t('common:remove')}
          />
          <Alert
            open={action === Action.Approve}
            onConfirm={handleContentApproval}
            onCancel={handleCancel}
            message={t('admin:bulkContentApproval', { count: actionCount })}
            confirm={t('common:approve')}
          />
        </Fragment>
      );
    }

    return (
      <Fragment>
        <Alert
          open={action === Action.Remove}
          onConfirm={handleContentRemoval}
          onCancel={handleCancel}
          message={t('admin:singleContentRemoval', { group: getGroupTitle() })}
          confirm={t('common:remove')}
        />
        <Alert
          open={action === Action.Approve}
          onConfirm={handleContentApproval}
          onCancel={handleCancel}
          message={t('admin:singleContentApproval', { group: getGroupTitle() })}
          confirm={t('common:approve')}
        />
      </Fragment>
    );
  }

  function renderRemovalIcon() {
    if (bulk) {
      if (isSubmitted) {
        return (
          <Grid item>
            <Tooltip placement="top" title={t('common:remove')}>
              <IconButton onClick={() => setAction(Action.Remove)}>
                <RemoveCircleRoundedIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        );
      }

      return null;
    }

    return (
      <Grid item>
        <Tooltip placement="top" title={t('common:remove')}>
          <IconButton onClick={() => setAction(Action.Remove)}>
            <RemoveCircleRoundedIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    );
  }

  function renderEditIcon() {
    if (bulk) {
      return null;
    }

    return (
      <Grid item>
        <Tooltip placement="top" title={t('common:edit')}>
          <IconButton
            component={generateLink(
              `/post/edit/${variables?.bulk_update.content_group_list[0].content_id}`
            )}
          >
            <CreateRoundedIcon />
          </IconButton>
        </Tooltip>
      </Grid>
    );
  }

  function renderApprovalIcon() {
    if (isSubmitted) {
      return (
        <Grid item>
          <Tooltip placement="top" title={t('common:approve')}>
            <IconButton onClick={() => setAction(Action.Approve)}>
              <CheckCircleRoundedIcon />
            </IconButton>
          </Tooltip>
        </Grid>
      );
    }

    return null;
  }

  return (
    <Fragment>
      {renderAlerts()}
      <Grid wrap="nowrap" container alignItems="center" justify="center">
        {renderRemovalIcon()}
        {renderEditIcon()}
        {renderApprovalIcon()}
      </Grid>
    </Fragment>
  );
};

export default Actions;
